package org.zq.astrock_ai.network

import org.zq.astrock_ai.util.formatDecimal

import org.zq.astrock_ai.api.ChartPeriod
import org.zq.astrock_ai.api.dto.AgentChatRequestDto
import org.zq.astrock_ai.api.dto.AgentChatResponseDto
import org.zq.astrock_ai.api.dto.BuyPlanDto
import org.zq.astrock_ai.api.dto.ConclusionDto
import org.zq.astrock_ai.api.dto.PositionViewDto
import org.zq.astrock_ai.api.dto.PriceZoneDto
import org.zq.astrock_ai.api.dto.QuantResultDto
import org.zq.astrock_ai.api.dto.RiskDto
import org.zq.astrock_ai.api.dto.SellPlanDto
import org.zq.astrock_ai.api.dto.StructuredAnalysisDto
import org.zq.astrock_ai.api.dto.TrendViewDto
import org.zq.astrock_ai.quant.DemoBars
import org.zq.astrock_ai.quant.QuantEngine
import org.zq.astrock_ai.quant.toDto
import org.zq.astrock_ai.ui.research.gushi.data.StockTrendMetrics
import org.zq.astrock_ai.ui.research.gushi.data.computeTrendMetrics
import org.zq.astrock_ai.ui.research.gushi.data.detectQuantStrategyId
import org.zq.astrock_ai.ui.research.gushi.data.toCandles
import org.zq.astrock_ai.ui.research.gushi.data.toQuantBars
import org.zq.astrock_ai.ui.research.gushi.data.toResearchStockItem
import org.zq.astrock_ai.ui.research.gushi.data.wantsQuantQuestion
import org.zq.astrock_ai.ui.research.gushi.model.Candle
import org.zq.astrock_ai.ui.research.gushi.model.StockItem
import org.zq.astrock_ai.ui.research.gushi.model.TrendDirection
import kotlin.random.Random

/**
 * 本地股势聊天引擎：不依赖 :server / Kimi。
 * 用东财行情 + 本地技术指标生成白话回复与结构化建议。
 */
class LocalStockChatEngine(
    private val market: MarketRepository = MarketRepository(),
) {
    suspend fun chat(
        request: AgentChatRequestDto,
        stockHint: StockItem? = null,
        metricsHint: StockTrendMetrics? = null,
    ): AgentChatResponseDto {
        val code = request.stockCode.filter { it.isDigit() }.takeLast(6).ifBlank { request.stockCode }
        val sessionId = request.sessionId?.takeIf { it.isNotBlank() }
            ?: "local-$code-${Random.nextInt(1000, 9999)}"

        if (request.message.isBlank()) {
            return AgentChatResponseDto(
                sessionId = sessionId,
                replyText = "请输入你想了解的问题，例如：现在能买吗？支撑位在哪里？",
                mode = "local",
                error = "empty_message",
            )
        }

        val kline = market.loadKline(code, ChartPeriod.DAY, 90)
        val candles = kline.bars.toCandles()
        val profile = market.loadWatchlist(listOf(code)).firstOrNull()
        val stock = stockHint?.takeIf { it.code == code }
            ?: profile?.toResearchStockItem()
            ?: stockFromCandles(code, request.stockName, candles)
        val metrics = metricsHint?.takeIf { stockHint?.code == code }
            ?: computeTrendMetrics(stock, candles)

        val structured = buildStructured(stock, metrics, candles)
        val wantQuant = wantsQuantQuestion(request.message)
        val strategyId = detectQuantStrategyId(request.message)
        val bars = candles.toQuantBars().ifEmpty {
            val seed = stock.price.replace(",", "").toDoubleOrNull() ?: 10.0
            DemoBars.synthesize(seed, days = 120, seed = code.hashCode())
        }
        val quantDto = if (wantQuant) {
            QuantEngine.buildPlan(bars, strategyId, lookbackDays = 90).toDto()
        } else {
            null
        }
        var reply = buildReply(
            question = request.message,
            stock = stock,
            metrics = metrics,
            candles = candles,
            cost = request.costPrice,
            quantity = request.quantity,
        )
        if (quantDto != null) {
            reply += "\n\n" + formatQuantReply(quantDto)
        }

        val tools = mutableListOf(
            "get_stock_snapshot",
            "get_stock_technical_state",
            "build_operation_plan",
        )
        if (wantQuant) {
            tools += listOf("list_strategies", "run_backtest", "get_quant_signal", "build_quant_plan")
        }

        return AgentChatResponseDto(
            sessionId = sessionId,
            replyText = reply,
            structured = structured,
            quant = quantDto,
            toolsCalled = tools,
            dataTime = candles.lastOrNull()?.date ?: "本地实时",
            mode = if (candles.isNotEmpty()) "local-live" else "local-demo",
        )
    }

    private fun formatQuantReply(q: QuantResultDto): String {
        val bt = q.backtest
        return buildString {
            appendLine("量化补充（本地引擎）")
            appendLine("策略：${q.strategyName}（${q.strategyId}）")
            appendLine("信号：${q.signal} · ${q.signalReason}")
            if (bt != null) {
                appendLine(
                    "回测（${bt.lookbackDays} 日）：${bt.trades} 笔，胜率 " +
                        "${formatDecimal(bt.winRate * 100, 0)}%，收益 " +
                        "${formatDecimal(bt.totalReturnPct)}%，最大回撤 " +
                        "${formatDecimal(bt.maxDrawdownPct)}%。",
                )
            }
            q.planSummary?.let { appendLine(it) }
            append("说明：回测为研究示意，不构成投资建议。")
        }
    }

    private fun stockFromCandles(code: String, name: String, candles: List<Candle>): StockItem {
        val last = candles.lastOrNull()
        val prev = candles.getOrNull(candles.lastIndex - 1)
        val price = last?.close ?: 0.0
        val change = if (prev != null && prev.close > 0) (price - prev.close) / prev.close * 100 else 0.0
        return StockItem(
            name = name.ifBlank { code },
            code = code,
            marketCode = if (code.startsWith("6")) "$code.SH" else "$code.SZ",
            price = formatDecimal(price),
            changeAmount = formatDecimal(price - (prev?.close ?: price), forceSign = true),
            changePercent = "${formatDecimal(change, forceSign = true)}%",
            direction = when {
                change > 0.01 -> TrendDirection.UP
                change < -0.01 -> TrendDirection.DOWN
                else -> TrendDirection.FLAT
            },
            status = "本地分析",
            note = "实时",
            industry = "—",
            tags = emptyList(),
            sparkline = emptyList(),
        )
    }

    private fun buildStructured(
        stock: StockItem,
        metrics: StockTrendMetrics,
        candles: List<Candle>,
    ): StructuredAnalysisDto {
        val support = metrics.support.toDoubleOrNull() ?: 0.0
        val resistance = metrics.resistance.toDoubleOrNull() ?: 0.0
        val price = stock.price.replace(",", "").toDoubleOrNull() ?: candles.lastOrNull()?.close ?: 0.0
        val upside = metrics.upsideProb.filter { it.isDigit() }.toIntOrNull()?.div(100.0) ?: 0.5
        return StructuredAnalysisDto(
            conclusion = ConclusionDto(
                action = when {
                    metrics.adviceTitle.contains("观望") || metrics.adviceTitle.contains("不追") -> "WAIT"
                    metrics.adviceTitle.contains("关注") -> "WATCH"
                    else -> "CAUTIOUS"
                },
                title = metrics.adviceTitle,
                summary = metrics.analysis,
            ),
            position = PositionViewDto(
                level = metrics.position,
                description = metrics.positionDesc,
            ),
            trend = TrendViewDto(
                shortTerm = metrics.status,
                mediumTerm = if (candles.size >= 20) "结合 MA20 观察" else "样本不足",
                confidence = upside,
            ),
            buyPlan = BuyPlanDto(
                enabled = !metrics.adviceTitle.contains("不追"),
                zones = listOf(
                    PriceZoneDto(
                        low = (support * 0.99).coerceAtLeast(0.01),
                        high = support * 1.01,
                        condition = "回踩支撑缩量企稳",
                    ),
                    PriceZoneDto(
                        low = resistance,
                        high = resistance * 1.02,
                        condition = "放量突破后回踩不破",
                    ),
                ),
            ),
            sellPlan = SellPlanDto(
                zones = listOf(
                    PriceZoneDto(
                        low = resistance * 0.99,
                        high = resistance * 1.03,
                        condition = "冲高乏力可分批止盈",
                    ),
                ),
            ),
            risk = RiskDto(
                invalidationPrice = support * 0.97,
                warnings = listOf(metrics.riskHint, "分析仅供研究参考，不构成投资建议"),
            ),
            evidence = listOf(
                "现价 ${stock.price}（${stock.changePercent}）",
                "状态 ${metrics.status} / 位置 ${metrics.position}",
                "支撑 ${metrics.support} / 压力 ${metrics.resistance}",
            ),
            dataTime = candles.lastOrNull()?.date ?: "本地",
        )
    }

    private fun buildReply(
        question: String,
        stock: StockItem,
        metrics: StockTrendMetrics,
        candles: List<Candle>,
        cost: Double?,
        quantity: Int?,
    ): String {
        val q = question.trim()
        val price = stock.price
        val name = stock.name
        val support = metrics.support
        val resistance = metrics.resistance
        val header = "【$name ${stock.code}】现价 $price（${stock.changePercent}）\n" +
            "状态：${metrics.status}｜位置：${metrics.position}｜风险：${metrics.riskLevel}\n" +
            "支撑 $support ／ 压力 $resistance\n" +
            "上涨概率 ${metrics.upsideProb}，转弱概率 ${metrics.weakenProb}\n"

        val body = when {
            q.contains("买") || q.contains("入手") || q.contains("建仓") ->
                "结论：${metrics.adviceTitle}。\n${metrics.buyPlan}\n\n${metrics.riskHint}"

            q.contains("卖") || q.contains("止盈") || q.contains("离场") ->
                "可在压力位 $resistance 附近观察是否冲高乏力；" +
                    "若收盘跌破支撑 $support，短线趋势转弱，宜减仓或观望。\n${metrics.riskHint}"

            q.contains("支撑") || q.contains("压力") || q.contains("位置") ->
                "近 20 日观察：支撑约 $support，压力约 $resistance。\n${metrics.positionDesc}"

            q.contains("高") || q.contains("贵") || q.contains("便宜") ->
                "当前位置判断为「${metrics.position}」。\n${metrics.analysis}"

            q.contains("新闻") || q.contains("消息") || q.contains("公告") ->
                "短线更应看量价是否确认，而不是标题本身。" +
                    "若股价已充分上涨，利好容易被提前计价；建议结合支撑 $support 的承接情况再决策。"

            q.contains("走势") || q.contains("趋势") || q.contains("怎么看") || q.contains("分析") ->
                metrics.analysis + "\n\n操作建议：${metrics.adviceTitle}\n${metrics.buyPlan}"

            cost != null || q.contains("成本") || q.contains("持仓") -> {
                val costVal = cost ?: Regex("""(\d+(?:\.\d+)?)""").find(q)?.groupValues?.getOrNull(1)?.toDoubleOrNull()
                val qty = quantity ?: 2500
                val px = price.replace(",", "").toDoubleOrNull()
                if (costVal != null && px != null) {
                    val pnl = (px - costVal) / costVal * 100
                    val pnlAmt = (px - costVal) * qty
                    "按成本 ${formatDecimal(costVal)}、数量 $qty 估算：\n" +
                        "浮动盈亏约 ${formatDecimal(pnl, forceSign = true)}%（约 ${formatDecimal(pnlAmt, 0, forceSign = true)} 元）。\n" +
                        "若浮盈，可在 $resistance 附近分批止盈；若跌破 $support，优先控制风险。\n" +
                        metrics.adviceTitle
                } else {
                    "请补充成本价，例如：成本 5.20 怎么办？"
                }
            }

            else ->
                "我已结合最新行情与近 20 日结构做了本地分析。\n" +
                    "${metrics.analysis}\n\n" +
                    "建议：${metrics.adviceTitle}\n${metrics.buyPlan}\n\n" +
                    "你也可以问：现在能买吗？支撑在哪？带着成本怎么看？"
        }

        val dataNote = if (candles.isNotEmpty()) {
            "\n\n数据截至 K 线 ${candles.last().date}（本地工具计算）"
        } else {
            "\n\n（行情样本不足，结论偏保守）"
        }

        return header + "\n" + body + dataNote
    }
}
